import type { MissionTaskType, TransformedMissionData } from '@tryhackme/interfaces';
import { TempCustomNotificationDispatch } from '@tryhackme/thm-ui-components';
import { MissionProgressNotification } from 'src/features/dashboard/components/missions/notifications';
import openMissionChest from 'src/images/pngs/dashboard/open-mission-chest.png';
import partiallyOpenMissionChest from 'src/images/pngs/dashboard/partial-mission-chest.png';

// Task display names for all mission types
const TASK_DISPLAY_NAMES: Record<MissionTaskType, string> = {
  completeQuestions: 'Questions answered',
  completeRooms: 'Rooms completed',
  earnPoints: 'Points earned',
  startVm: 'Virtual machine started',
  streakDays: 'Login streak'
};

/**
 * Displays mission progress notifications for any task type
 * @param mission - Current mission data
 * @param taskTypes - Array of task types that were just completed
 * @returns boolean - Whether any notification was shown
 */
export const displayMissionProgressNotification = (mission: TransformedMissionData, taskTypes: MissionTaskType[]): boolean => {
  // don't show any notifications (prevents spam for already completed missions)
  if (mission.completedAt) {
    const allTasksCompleted = mission.tasks.every(task => task.countCompleted >= task.count);
    if (allTasksCompleted) {
      return false;
    }
  }
  let notificationShown = false;
  let willCompleteMission = false;

  // Process each completed task type
  for (const taskType of taskTypes) {
    const missionTask = mission.tasks.find(task => task.type === taskType);

    // Skip if user doesn't have this task
    if (!missionTask) {
      continue;
    }

    // Calculate what the new progress should be after this action
    const newProgress = Math.min(missionTask.countCompleted + 1, missionTask.count);
    const isTaskCompleted = newProgress >= missionTask.count;
    const taskName = TASK_DISPLAY_NAMES[taskType] || taskType;

    // Skip if this action wouldn't change anything (task was already completed)
    if (missionTask.countCompleted >= missionTask.count) {
      continue;
    }

    // Check if completing this task would complete the entire mission
    const updatedTasks = mission.tasks.map(task => task.type === taskType ? {
      ...task,
      countCompleted: newProgress
    } : task);
    willCompleteMission = updatedTasks.every(task => task.countCompleted >= task.count);

    // Show appropriate notification (task progress or mission completion)
    TempCustomNotificationDispatch({
      children: <MissionProgressNotification taskName={taskName} progressText={`${newProgress}/${missionTask.count}`} isTaskCompleted={isTaskCompleted} />,
      options: {
        icon: <img src={isTaskCompleted ? openMissionChest : partiallyOpenMissionChest} alt="Weekly Mission" width={64} height={64} />,
        className: willCompleteMission ? 'mission-completed-toast' : 'mission-progress-toast',
        style: {
          borderRadius: '5px',
          maxWidth: '450px'
        },
        autoClose: willCompleteMission ? 6000 : 3000 // Longer duration for mission completion
      }
    });
    notificationShown = true;

    // Only show one notification at a time (prioritize first task in array)
    break;
  }
  return notificationShown;
};